import { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import { validarRangoFechas } from "../utils/validarRangoFechas";
import {
  getConcesionesPeajeService,
  getPeajesPorConcesionService,
  getCuadrePeajesService,
} from "modules/finops/conciliaciones/services/peajesConciliacionService";

// ============================================================
// HELPERS
// ============================================================
const ordenarPorFechaAsc = (filas) =>
  [...filas].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));

const sumar = (arr, key) => arr.reduce((acc, item) => acc + (Number(item[key]) || 0), 0);

const tieneImporte = (value) =>
  value != null && String(value).trim() !== "" && Number.isFinite(Number(value));
const estadoCuadre = (peaje) =>
  String(peaje.estado_cuadre || "")
    .trim()
    .toUpperCase();
const pagoPorPeaje = (peaje, cuadrado, hayDiferencia) => {
  const value = cuadrado ? peaje.valor_concesion : hayDiferencia ? peaje.value_finops : null;
  if (!tieneImporte(value)) return {};
  return {
    pagoFinops: true,
    pagoFinopsCuadrado: cuadrado,
    pagoFinanciera: Number(value),
    diferenciaFinanciera: Number(peaje.total_gopass || 0) - Number(value),
  };
};

export const useTablaGestionConciliaciones = () => {
  const [concesiones, setConcesiones] = useState([]);
  const [peajes, setPeajes] = useState([]);

  const [selectedConcesion, setSelectedConcesion] = useState("");
  const [selectedPeaje, setSelectedPeaje] = useState("");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(null);
  const [selectedFechaFin, setSelectedFechaFin] = useState(null);

  const [error, setError] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPeajes, setLoadingPeajes] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [datosTabla, setDatosTabla] = useState([]);
  const [datosResumen, setDatosResumen] = useState([]);
  const [totales, setTotales] = useState({});
  const [totalesGenerales, setTotalesGenerales] = useState({});
  const [resumenDiferencias, setResumenDiferencias] = useState([]);
  const [modoAgrupado, setModoAgrupado] = useState(false);

  // Cargar concesiones al montar
  useEffect(() => {
    const cargarConcesiones = async () => {
      setLoading(true);
      const result = await getConcesionesPeajeService();
      if (result.success) setConcesiones(result.data);
      else setConcesiones([]);
      setLoading(false);
    };
    cargarConcesiones();
  }, []);

  // Solo la concesión vigente puede actualizar la lista de peajes.
  useEffect(() => {
    let active = true;
    setPeajes([]);
    setSelectedPeaje("");
    setLoadingPeajes(false);
    const concesionObj = concesiones.find((c) => c.nombre === selectedConcesion);
    if (!selectedConcesion || !concesionObj?.codigooperador) return;

    const cargarPeajes = async () => {
      setLoadingPeajes(true);
      try {
        const result = await getPeajesPorConcesionService(concesionObj.codigooperador);
        if (active) setPeajes(result.success ? result.data : []);
      } catch (error) {
        if (active) setPeajes([]);
      } finally {
        if (active) setLoadingPeajes(false);
      }
    };
    cargarPeajes();
    return () => {
      active = false;
    };
  }, [selectedConcesion, concesiones]);

  // Buscar
  const handleBuscar = useCallback(async () => {
    if (loading || !selectedConcesion || loadingPeajes || !selectedFechaInicio || !selectedFechaFin)
      return;
    const errors = validarRangoFechas(selectedFechaInicio, selectedFechaFin);
    if (errors.fechaInicio || errors.fechaFin) return;

    setError("");
    setLoading(true);
    setShowResults(false);

    try {
      const result = await getCuadrePeajesService({
        concesion: selectedConcesion,
        peaje: selectedPeaje || undefined,
        fecha_inicio: selectedFechaInicio
          ? dayjs(selectedFechaInicio).format("YYYY-MM-DD")
          : undefined,
        fecha_fin: selectedFechaFin ? dayjs(selectedFechaFin).format("YYYY-MM-DD") : undefined,
      });

      if (result.success) {
        const ajustesPagoFinops = [];
        setAppliedFilters({
          concesion: selectedConcesion,
          peaje: selectedPeaje,
          inicio: dayjs(selectedFechaInicio).format("DD/MM/YYYY"),
          fin: dayjs(selectedFechaFin).format("DD/MM/YYYY"),
        });
        // ==========================================================
        // CASO 1: Se seleccionó peaje específico → tabla plana
        // ==========================================================
        if (selectedPeaje) {
          const filas = (result.data || []).map((item) => ({
            tipoFila: "detalle",
            fecha: item.fecha,
            peaje: item.peaje,
            concesion: item.concesion,
            codigooperador: item.codigooperador,
            gopassPasosExitosos: item.gopass_pasos_exitosos,
            cantExitosos: item.cant_exitosos,
            valorConcesion: item.valor_concesion,
            pasosConcesion: item.pasos_concesion,
            diferenciaInicial: item.diferencia_inicial,
            pagosRechazos: item.pagos_rechazos,
            cantRechazos: item.cant_rechazos,
            totalGopass: item.total_gopass,
            totalPasos: item.total_pasos,
            diferenciaFinal: item.diferencia_final,
            pagoFinanciera: item.pago_financiera,
            diferenciaFinanciera: item.diferencia_financiera,
            valoresAdicionales: item.valores_adicionales,
            descuentos: item.descuentos,
            descuento: item.descuento,
            estadoCuadre: item.estado_cuadre,
            fechaPagoBanco: item.fecha_pago_banco,
            observacion: item.observacion,
            ...pagoPorPeaje(
              item,
              estadoCuadre(item) === "CUADRADO",
              estadoCuadre(item) === "CON_DIFERENCIA"
            ),
          }));

          const filasOrdenadas = ordenarPorFechaAsc(filas);
          setDatosTabla(filasOrdenadas);
          setModoAgrupado(false);
        } else {
          // ==========================================================
          // CASO 2: NO se seleccionó peaje → vista agrupada por día
          // ==========================================================
          const filas = [];

          const resumenDiarioOrdenado = [...(result.resumenDiario || [])].sort((a, b) =>
            (a.fecha || "").localeCompare(b.fecha || "")
          );

          resumenDiarioOrdenado.forEach((dia) => {
            const peajesDia = [...(dia.peajes || [])].sort((a, b) =>
              (a.peaje || "").localeCompare(b.peaje || "")
            );

            const sub = dia.subtotal_dia || {};
            const allStatus = (status) =>
              peajesDia.length > 0 && peajesDia.every((p) => estadoCuadre(p) === status);
            const pagoFinopsCuadrado = allStatus("CUADRADO");
            const hayDiferencia = peajesDia.some((p) => estadoCuadre(p) === "CON_DIFERENCIA");
            const pagosPeajes = peajesDia.map((p) =>
              pagoPorPeaje(p, pagoFinopsCuadrado, hayDiferencia)
            );
            const todosConPago = pagosPeajes.length > 0 && pagosPeajes.every((p) => p.pagoFinops);
            const valueFinal = todosConPago
              ? sumar(pagosPeajes, "pagoFinanciera")
              : sub.value_finops ??
                dia.value_finops ??
                (allStatus("CON_DIFERENCIA") ? sub.value_final ?? dia.value_final : null);
            const pagoFinops = (pagoFinopsCuadrado || hayDiferencia) && tieneImporte(valueFinal);

            peajesDia.forEach((p, index) => {
              filas.push({
                tipoFila: "detalle",
                fecha: p.fecha,
                peaje: p.peaje,
                concesion: p.concesion,
                codigooperador: p.codigooperador,
                gopassPasosExitosos: p.gopass_pasos_exitosos,
                cantExitosos: p.cant_exitosos,
                valorConcesion: p.valor_concesion,
                pasosConcesion: p.pasos_concesion,
                diferenciaInicial: p.diferencia_inicial,
                pagosRechazos: p.pagos_rechazos,
                cantRechazos: p.cant_rechazos,
                totalGopass: p.total_gopass,
                totalPasos: p.total_pasos,
                diferenciaFinal: p.diferencia_final,
                pagoFinanciera: p.pago_financiera,
                diferenciaFinanciera: p.diferencia_financiera,
                valoresAdicionales: p.valores_adicionales,
                descuentos: p.descuentos,
                descuento: p.descuento,
                estadoCuadre: p.estado_cuadre,
                fechaPagoBanco: p.fecha_pago_banco,
                observacion: p.observacion,
                ...pagosPeajes[index],
              });
            });

            if (pagoFinops)
              ajustesPagoFinops.push({
                cuadrado: pagoFinopsCuadrado,
                pago: Number(valueFinal) - sumar(peajesDia, "pago_financiera"),
                diferencia:
                  Number(sub.total_gopass ?? sumar(peajesDia, "total_gopass")) -
                  Number(valueFinal) -
                  sumar(peajesDia, "diferencia_financiera"),
              });
            filas.push({
              tipoFila: "subtotal",
              fecha: dia.fecha,
              peaje: "TOTAL DÍA",
              concesion: "",
              codigooperador: null,
              gopassPasosExitosos: sub.gopass_pasos_exitosos,
              cantExitosos: sub.cant_exitosos,
              valorConcesion: sub.valor_concesion,
              pasosConcesion: sub.pasos_concesion,
              diferenciaInicial: sub.diferencia_inicial,
              pagosRechazos: sub.pagos_rechazos,
              cantRechazos: sub.cant_rechazos,
              totalGopass: sub.total_gopass,
              totalPasos: sub.total_pasos ?? sumar(peajesDia, "total_pasos"),
              diferenciaFinal: sub.diferencia_final,
              pagoFinops,
              pagoFinopsCuadrado: pagoFinops && pagoFinopsCuadrado,
              pagoFinanciera: pagoFinops ? Number(valueFinal) : sub.pago_financiera,
              diferenciaFinanciera: pagoFinops
                ? Number(sub.total_gopass ?? sumar(peajesDia, "total_gopass")) - Number(valueFinal)
                : sub.diferencia_financiera,
              valoresAdicionales: sub.valores_adicionales,
              descuentos: sub.descuento,
              descuento: sub.descuento,
            });
          });

          setDatosTabla(filas);
          setModoAgrupado(true);
        }

        // ======================================================
        // TOTALES GENERALES
        // ======================================================
        const todasLasFilas = [];
        if (selectedPeaje) {
          (result.data || []).forEach((item) => {
            todasLasFilas.push({
              tipoFila: "detalle",
              peaje: item.peaje,
              totalPasos: item.total_pasos,
              gopassPasosExitosos: item.gopass_pasos_exitosos,
              valorConcesion: item.valor_concesion,
              diferenciaInicial: item.diferencia_inicial,
              pagosRechazos: item.pagos_rechazos,
              totalGopass: item.total_gopass,
              diferenciaFinal: item.diferencia_final,
              pagoFinanciera: item.pago_financiera,
              diferenciaFinanciera: item.diferencia_financiera,
              valoresAdicionales: item.valores_adicionales,
              descuentos: item.descuentos,
              ...pagoPorPeaje(
                item,
                estadoCuadre(item) === "CUADRADO",
                estadoCuadre(item) === "CON_DIFERENCIA"
              ),
            });
          });
        } else {
          (result.resumenDiario || []).forEach((dia) => {
            (dia.peajes || []).forEach((p) => {
              todasLasFilas.push({
                tipoFila: "detalle",
                peaje: p.peaje,
                totalPasos: p.total_pasos,
                gopassPasosExitosos: p.gopass_pasos_exitosos,
                valorConcesion: p.valor_concesion,
                diferenciaInicial: p.diferencia_inicial,
                pagosRechazos: p.pagos_rechazos,
                totalGopass: p.total_gopass,
                diferenciaFinal: p.diferencia_final,
                pagoFinanciera: p.pago_financiera,
                diferenciaFinanciera: p.diferencia_financiera,
                valoresAdicionales: p.valores_adicionales,
                descuentos: p.descuentos,
              });
            });
          });
        }

        const tg = {
          totalPasos: sumar(todasLasFilas, "totalPasos"),
          gopassPasosExitosos: sumar(todasLasFilas, "gopassPasosExitosos"),
          valorConcesion: sumar(todasLasFilas, "valorConcesion"),
          diferenciaInicial: sumar(todasLasFilas, "diferenciaInicial"),
          pagosRechazos: sumar(todasLasFilas, "pagosRechazos"),
          totalGopass: sumar(todasLasFilas, "totalGopass"),
          diferenciaFinal: sumar(todasLasFilas, "diferenciaFinal"),
          pagoFinanciera: sumar(todasLasFilas, "pagoFinanciera"),
          diferenciaFinanciera: sumar(todasLasFilas, "diferenciaFinanciera"),
          valoresAdicionales: sumar(todasLasFilas, "valoresAdicionales"),
          descuentos: sumar(todasLasFilas, "descuentos"),
        };

        if (selectedPeaje && todasLasFilas.some((fila) => fila.pagoFinops)) {
          tg.pagoFinops = true;
          tg.pagoFinopsCuadrado = todasLasFilas
            .filter((fila) => fila.pagoFinops)
            .every((fila) => fila.pagoFinopsCuadrado);
        }
        if (ajustesPagoFinops.length) {
          tg.pagoFinanciera += sumar(ajustesPagoFinops, "pago");
          tg.diferenciaFinanciera += sumar(ajustesPagoFinops, "diferencia");
          tg.pagoFinops = true;
          tg.pagoFinopsCuadrado = ajustesPagoFinops.every((ajuste) => ajuste.cuadrado);
        }
        setTotalesGenerales(tg);

        // ======================================================
        // DIFERENCIAS
        // ======================================================
        const diferencias = [
          {
            label: "Valor a pagar financiera",
            value: tg.pagoFinanciera,
          },
          {
            label: "Total Gopass − Pago financiera",
            minuendo: tg.totalGopass,
            sustraendo: tg.pagoFinanciera,
            value: tg.totalGopass - tg.pagoFinanciera,
          },
          {
            label: "Pasos exitosos Gopass − Valor concesión",
            minuendo: tg.gopassPasosExitosos,
            sustraendo: tg.valorConcesion,
            value: tg.gopassPasosExitosos - tg.valorConcesion,
          },
          {
            label: "Valor concesión − Pagos rechazados",
            minuendo: tg.valorConcesion,
            sustraendo: tg.pagosRechazos,
            value: tg.valorConcesion - tg.pagosRechazos,
          },
          {
            label: "Pago financiera − Pasos exitosos Gopass",
            minuendo: tg.pagoFinanciera,
            sustraendo: tg.gopassPasosExitosos,
            value: tg.pagoFinanciera - tg.gopassPasosExitosos,
          },
        ];
        setResumenDiferencias(diferencias);

        // ======================================================
        // RESUMEN POR PEAJE (Total Pago = suma total_gopass)
        // ======================================================
        const resumenMap = {};
        todasLasFilas.forEach((f) => {
          if (!resumenMap[f.peaje]) {
            resumenMap[f.peaje] = {
              peaje: f.peaje,
              totalPasos: 0,
              totalRechazado: 0,
              totalPago: 0,
            };
          }
          resumenMap[f.peaje].totalPasos += Number(f.totalPasos) || 0;
          resumenMap[f.peaje].totalRechazado += Number(f.pagosRechazos) || 0;
          resumenMap[f.peaje].totalPago += Number(f.totalGopass) || 0;
        });

        const resumenArr = Object.values(resumenMap)
          .map((r) => ({
            peaje: r.peaje,
            totalPasos: r.totalPasos,
            totalRechazado: r.totalRechazado,
            totalPago: r.totalPago,
          }))
          .sort((a, b) => (a.peaje || "").localeCompare(b.peaje || ""));

        setDatosResumen(resumenArr);
        setTotales(result.totales || {});
        setShowResults(true);
      } else {
        setError(
          result.error ||
            result.message ||
            "No se pudieron consultar las conciliaciones. Intenta nuevamente."
        );
        setDatosTabla([]);
        setDatosResumen([]);
        setTotales({});
        setTotalesGenerales({});
        setResumenDiferencias([]);
        setShowResults(true);
      }
    } catch (error) {
      setError(error.message || "No se pudieron consultar las conciliaciones. Intenta nuevamente.");
      setDatosTabla([]);
      setDatosResumen([]);
      setTotales({});
      setTotalesGenerales({});
      setResumenDiferencias([]);
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  }, [
    selectedConcesion,
    selectedPeaje,
    selectedFechaInicio,
    selectedFechaFin,
    loadingPeajes,
    loading,
  ]);

  const handleLimpiarTodo = useCallback(() => {
    setError("");
    setAppliedFilters(null);
    setSelectedConcesion("");
    setSelectedPeaje("");
    setSelectedFechaInicio(null);
    setSelectedFechaFin(null);
    setDatosTabla([]);
    setDatosResumen([]);
    setTotales({});
    setTotalesGenerales({});
    setResumenDiferencias([]);
    setModoAgrupado(false);
    setShowResults(false);
  }, []);

  return {
    error,
    appliedFilters,
    concesiones,
    peajes,
    selectedConcesion,
    selectedPeaje,
    selectedFechaInicio,
    selectedFechaFin,
    loading,
    loadingPeajes,
    showResults,
    datosTabla,
    datosResumen,
    totales,
    totalesGenerales,
    resumenDiferencias,
    modoAgrupado,
    setSelectedConcesion,
    setSelectedPeaje,
    setSelectedFechaInicio,
    setSelectedFechaFin,
    handleBuscar,
    handleLimpiarTodo,
  };
};
